import type { PrimaryFilterId } from '../../../../filters/types/filters';
import {
  type DataProviderSet,
  DataType,
  isBoxBreakdownDataSet,
} from '../../../DataProvider/types';
import type { SecondaryDimensionValues } from '../../../PageConfigProvider/types/dimension';
import {
  isFromGroup,
  isPayPercentileMetric,
  type MetricValues,
} from '../../../PageConfigProvider/types/metric';

export type TableRow = Record<string, unknown>;

export type ColumnMeta = { metric?: MetricValues; format?: string };
export type ColumnMetaMap = Record<string, ColumnMeta>;

export type TableConversionResult = {
  rows: TableRow[];
  columnMetaMap: ColumnMetaMap;
};

type TransitionBreakdownRow = {
  dim: string;
  value: number | null;
  primary?: string;
};

export type CompensationSingleRow = {
  dim: string;
  value: number | null;
  p10?: number | null;
  p25?: number | null;
  p50?: number | null;
  p75?: number | null;
  p90?: number | null;
};

/**
 * Converts metric names to display format
 * @param str - The metric string to parse (e.g., "hiring_rate")
 * @param capitalizeAll - Whether to capitalize all words (default: false, only first letter)
 * @returns Formatted string (e.g., "Hiring rate" or "Hiring Rate")
 */
export const parseMetricName = (str: string, capitalizeAll = false) => {
  const parsedStr = str.replace(/^screener_/, '').replace(/_/g, ' ');
  if (capitalizeAll) {
    return parsedStr.replace(/\b\w/g, (l) => l.toUpperCase());
  }
  return parsedStr.charAt(0).toUpperCase() + parsedStr.slice(1);
};

// Keep the old function for backward compatibility
const parseString = (str: string) => parseMetricName(str, false);

const PERCENTILE_COLUMNS = [
  { key: 'p10' as const, label: 'P10' },
  { key: 'p25' as const, label: 'P25' },
  { key: 'p50' as const, label: 'P50' },
  { key: 'p75' as const, label: 'P75' },
  { key: 'p90' as const, label: 'P90' },
];

export const convertSeriesToTableData = ({
  series,
  metric,
  secondaryDimension,
  primaryDimension,
}: {
  series: DataProviderSet;
  metric: MetricValues;
  secondaryDimension: SecondaryDimensionValues | undefined;
  primaryDimension?: PrimaryFilterId;
}): TableConversionResult => {
  const empty: TableConversionResult = { rows: [], columnMetaMap: {} };
  if (!series) {
    return empty;
  }

  if (series.type === DataType.MAP) {
    const points = series?.points ?? [];
    return { rows: points, columnMetaMap: {} };
  }

  if (!series.data) {
    return empty;
  }

  switch (series.type) {
    case DataType.TABLE_ROWS: {
      const rows = series.data as TableRow[];
      const metaMap: ColumnMetaMap = {};
      const firstRow = rows[0];
      const columnMetrics = firstRow?.__columnMetrics as
        | Record<string, string>
        | undefined;
      const columnFormats = firstRow?.__columnFormats as
        | Record<string, string>
        | undefined;
      if (firstRow) {
        for (const key of Object.keys(firstRow)) {
          if (key.startsWith('__')) continue;
          if (typeof firstRow[key] === 'number' || firstRow[key] === null) {
            metaMap[key] = {
              metric: (columnMetrics?.[key] ?? metric) as MetricValues,
              ...(columnFormats?.[key] && { format: columnFormats[key] }),
            };
          }
        }
      }
      // Strip internal keys from rows
      const hasInternalKeys = columnMetrics || columnFormats;
      const cleanRows = hasInternalKeys
        ? rows.map((row) => {
            const clean: Record<string, unknown> = {};
            for (const [k, v] of Object.entries(
              row as Record<string, unknown>
            )) {
              if (!k.startsWith('__')) clean[k] = v;
            }
            return clean as TableRow;
          })
        : rows;
      return { rows: cleanRows, columnMetaMap: metaMap };
    }
    case DataType.SINGLE: {
      const column1Name = (() => {
        if (isFromGroup('Talent Discovery', metric)) {
          if (primaryDimension === 'COMPANY') return 'Company';
          if (primaryDimension === 'ROLE') return 'Role';
          if (primaryDimension === 'GEOGRAPHY') return 'Location';
        }
        return 'Primary';
      })();

      const column2Name = (() => {
        if (metric === 'td_users') return 'Employees';
        return parseString(metric);
      })();

      const columnMetaMap: ColumnMetaMap = {
        [column1Name]: {},
        [column2Name]: { metric },
      };
      PERCENTILE_COLUMNS.forEach(({ label }) => {
        columnMetaMap[label] = { metric };
      });

      const rows = series.data.map((point) => {
        const compPoint = point as unknown as CompensationSingleRow;
        const hasPercentiles =
          isPayPercentileMetric(metric) ||
          compPoint.p10 != null ||
          compPoint.p25 != null ||
          compPoint.p50 != null ||
          compPoint.p75 != null ||
          compPoint.p90 != null;

        const base: TableRow = {
          [column1Name]: point.dim,
        };

        if (hasPercentiles) {
          PERCENTILE_COLUMNS.forEach(({ key, label }) => {
            const v = compPoint[key];
            if (v != null) base[label] = v;
          });
        } else {
          base[column2Name] = point.value;
        }

        return base;
      });
      return { rows, columnMetaMap };
    }

    case DataType.TIMESERIES: {
      const rows = series.data.flatMap((entry) =>
        entry.timeseries.map((ts) => ({
          label: entry.dim,
          date: ts.date,
          value: ts.value,
        }))
      );
      return {
        rows,
        columnMetaMap: { value: { metric } },
      };
    }

    case DataType.BREAKDOWN_SINGLE: {
      const column1Name = (() => {
        if (isFromGroup('Postings', metric) && secondaryDimension) {
          if (primaryDimension === undefined) return 'Primary';
          if (primaryDimension === 'COMPANY') return 'Role';
          return 'Company';
        }

        if (isFromGroup('Transitions', metric) && secondaryDimension) {
          return 'Status';
        }

        return 'Primary';
      })();

      const column2Name = (() => {
        if (isFromGroup('Postings', metric) && secondaryDimension) {
          return 'Primary';
        }

        if (isFromGroup('Transitions', metric)) {
          if (secondaryDimension) {
            return parseString(secondaryDimension);
          }

          return metric === 'transitions_inflow' ? 'From' : 'To';
        }

        if (secondaryDimension) {
          return parseString(secondaryDimension);
        }

        return 'Date';
      })();

      const metricHeader =
        secondaryDimension && isFromGroup('Postings', metric)
          ? 'Postings'
          : parseString(metric);

      const columnMetaMap: ColumnMetaMap = {
        [column1Name]: {},
        [column2Name]: {},
        [metricHeader]: { metric },
        Primary: {},
      };
      PERCENTILE_COLUMNS.forEach(({ label }) => {
        columnMetaMap[label] = { metric };
      });

      const data = series.data.flatMap((entry) =>
        entry.breakdown.map((b) => {
          const transitionRow = b as unknown as TransitionBreakdownRow;
          const compRow = b as unknown as CompensationSingleRow;
          const { primary = '', value } = transitionRow;

          const hasPercentiles =
            isPayPercentileMetric(metric) ||
            compRow.p10 != null ||
            compRow.p25 != null ||
            compRow.p50 != null ||
            compRow.p75 != null ||
            compRow.p90 != null;

          const base: TableRow = {
            [column1Name]: entry.dim,
            [column2Name]: b.dim,
          };

          if (hasPercentiles) {
            PERCENTILE_COLUMNS.forEach(({ key, label }) => {
              const v = compRow[key];
              if (v != null) base[label] = v;
            });
          } else {
            base[metricHeader] = value;
          }

          return primary ? { Primary: primary, ...base } : base;
        })
      );

      const columns = Object.keys(data[0] || {});
      const columnsToRemove = columns.filter((col) =>
        data.every(
          (row) =>
            row[col] === null || row[col] === undefined || row[col] === ''
        )
      );

      const rows = data.map((row) =>
        Object.fromEntries(
          Object.entries(row).filter(([key]) => !columnsToRemove.includes(key))
        )
      );
      return { rows, columnMetaMap };
    }

    case DataType.BOX: {
      if (!isBoxBreakdownDataSet(series)) {
        return empty;
      }
      const rows = series.data.flatMap((entry) =>
        entry.breakdown.map((b) => ({
          primary: entry.dim,
          category: b.dim,
          q10: b.box.q10,
          q50: b.box.q50,
          q90: b.box.q90,
        }))
      );
      return {
        rows,
        columnMetaMap: { q10: { metric }, q50: { metric }, q90: { metric } },
      };
    }

    case DataType.DISTRIBUTION: {
      const rows = series.data.flatMap((entry) =>
        entry.distribution.map((d) => ({
          label: entry.dim,
          value: d.value,
          kde: d.kde,
          cdf: d.cdf,
        }))
      );
      return {
        rows,
        columnMetaMap: { value: { metric }, kde: { metric }, cdf: { metric } },
      };
    }

    default:
      return empty;
  }
};

export const convertMultiMetricSeriesToTableData = ({
  seriesByMetric,
  secondaryDimension,
  primaryDimension,
}: {
  seriesByMetric: Partial<Record<MetricValues, DataProviderSet | undefined>>;
  secondaryDimension: SecondaryDimensionValues | undefined;
  primaryDimension?: PrimaryFilterId;
}): TableConversionResult => {
  const empty: TableConversionResult = { rows: [], columnMetaMap: {} };
  const metrics = Object.keys(seriesByMetric) as MetricValues[];

  if (metrics.length === 0) {
    return empty;
  }

  const firstMetric = metrics[0];
  if (!firstMetric) return empty;
  const firstSeries = seriesByMetric[firstMetric];

  if (!firstSeries) {
    return empty;
  }

  const baseResult = convertSeriesToTableData({
    series: firstSeries,
    metric: firstMetric,
    secondaryDimension,
    primaryDimension,
  });

  if (baseResult.rows.length === 0) {
    return empty;
  }

  const columnMetaMap: ColumnMetaMap = { ...baseResult.columnMetaMap };
  const metricDataMap = new Map<MetricValues, Map<string, TableRow>>();

  for (let i = 1; i < metrics.length; i++) {
    const metric = metrics[i];
    if (!metric) continue;

    const series = seriesByMetric[metric];
    if (!series) continue;

    const metricResult = convertSeriesToTableData({
      series,
      metric,
      secondaryDimension,
      primaryDimension,
    });

    const primaryKeyMap = new Map<string, TableRow>();
    metricResult.rows.forEach((row) => {
      const primaryKeys = [
        'Company',
        'Role',
        'Location',
        'Primary',
        'dim',
        'label',
      ];
      const primaryKey = primaryKeys.find((key) => row[key] !== undefined);
      if (primaryKey && row[primaryKey] !== undefined) {
        primaryKeyMap.set(
          String(
            `${row[primaryKey]}_${row[parseMetricName(secondaryDimension ?? '')]}`
          ),
          row
        );
      }
    });
    metricDataMap.set(metric, primaryKeyMap);
  }

  const mergedData = baseResult.rows.map((baseRow) => {
    const mergedRow = { ...baseRow };

    for (let i = 1; i < metrics.length; i++) {
      const metric = metrics[i];
      if (!metric) continue;

      const primaryKeyMap = metricDataMap.get(metric);
      if (!primaryKeyMap) continue;

      const primaryKeys = [
        'Company',
        'Role',
        'Location',
        'Primary',
        'dim',
        'label',
      ];
      const primaryKey = primaryKeys.find((key) => baseRow[key] !== undefined);
      const matchingRow = primaryKey
        ? primaryKeyMap.get(
            String(
              `${baseRow[primaryKey]}_${baseRow[parseMetricName(secondaryDimension ?? '')]}`
            )
          )
        : undefined;

      if (matchingRow) {
        const secondaryDimKey = parseMetricName(secondaryDimension ?? '');
        Object.entries(matchingRow).forEach(([key, value]) => {
          if (
            key !== 'Primary' &&
            key !== 'Company' &&
            key !== 'Role' &&
            key !== 'Location' &&
            key !== 'label' &&
            key !== 'date' &&
            key !== 'dim' &&
            key !== secondaryDimKey
          ) {
            const parsedMetricName = parseString(metric);
            const metricColumnName =
              key === parsedMetricName ? key : `${parsedMetricName}_${key}`;
            mergedRow[metricColumnName] = value;
            columnMetaMap[metricColumnName] = { metric };
          }
        });
      }
    }

    return mergedRow;
  });

  return { rows: mergedData, columnMetaMap };
};
